import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { useAppSelector } from 'store';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats, ModuleType } from 'infrastructure/enums';

import type { SortState } from 'infrastructure/types';
import type {
  IManualClinicNotesResponse,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';

interface Props {
  patientGuid: string;
  meta: IPartialPaginationMeta;
  module: ModuleType;
  documentationTimeSort?: SortState;
}

type TFilter = {
  patientGuid: string;
  startTime?: string;
  endTime?: string;
  module: ModuleType;
  documentationTimeSort?: string;
} & IPartialPaginationMeta;

export const getManualClinicNotesFn = async ({
  patientGuid,
  module,
  ...filter
}: TFilter) => {
  let url = `core/ccm/patients/${patientGuid}/manual-time`;
  if (module === ModuleType.RPM) {
    url = `core/patients/${patientGuid}/manual-time`;
  }
  const { data, error } = await dataService.getList<IManualClinicNotesResponse>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  if (data) {
    const [count, items] = Object.values(data);

    return { count, items } as IManualClinicNotesResponse;
  }
};

export const getManualClinicNotesKey = (filter: TFilter) => [
  'getManualClinicNotes',
  filter,
];

export const MANUAL_CLINIC_NOTES_PER_PAGE = 5;

export const useGetManualClinicNotes = ({
  patientGuid,
  meta,
  module,
  documentationTimeSort,
}: Props) => {
  const [startTime, endTime] = useAppSelector<[Date, Date] | []>(
    (state) => state.ccmPatientDetails.dates,
  );

  const filter: TFilter = {
    ...meta,
    patientGuid,
    items: MANUAL_CLINIC_NOTES_PER_PAGE,
    startTime: DateFormatter({
      date: startTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    endTime: DateFormatter({
      date: endTime,
      format: DateFormats['yyyy-MM-dd HH:mm:s'],
    }),
    module,
    documentationTimeSort: documentationTimeSort?.toUpperCase(),
  };

  const {
    data: manualClinicNotes,
    isLoading,
    isFetching,
    isRefetching,
    ...other
  } = useQuery({
    queryKey: getManualClinicNotesKey(filter),
    queryFn: async () => getManualClinicNotesFn({ ...filter }),
    enabled: Boolean(startTime) && Boolean(endTime),
  });

  return {
    manualClinicNotes,
    isLoading: isLoading || isFetching || isRefetching,
    isFetching,
    ...other,
  };
};
